import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { FileText, ChevronDown } from 'lucide-react';
import { IconClose } from '../../icons';
import ModalDefault from '../../ModalDefault';
import { ModalContactHead, ModalcontactMain } from '../../SearchProfessionalProposal/styles';
import { comum } from '../../../styles/Themes/light';
import { useAuth } from '../../../contexts/UserContext';
import api from '../../../services/api';
import toast from 'react-hot-toast';
import { toastDefs, toastDefsError } from '../../../config/toastConfig';
import InputMoney from '../../form/InputMoney';
import styled from 'styled-components';
import { CloseButtonHeader, ModalSection, Label, TextArea, ButtonGroup, Button } from './modalStyles';
import { TIPOS_PENDENCIA, TIPOS_PENDENCIA_LABELS, TIPOS_PENDENCIA_DESCRICOES, TIPOS_PENDENCIA_LABELS_DESCRICAO, getTipoIcon } from './utils';
import { IProposta } from '../../../utility/IProposta';

interface ModalCriarPendenciaProps {
    modalAtivo: boolean;
    setModalAtivo: (ativo: boolean) => void;
    id_proposta: number;
    onPendenciaCriada?: () => void;
}

interface TipoPendencia {
    id: string;
    nome: string;
    icon: React.ElementType;
    labelDescricao: string;
    temValor: boolean;
}

interface PropostaCompleta extends IProposta {
    nomeProfissional?: string;
    emailProfissional?: string;
    email_escritorio?: string;
}

type DestinatarioTipo = 'correspondente' | 'solicitante' | null;

const VALOR_INICIAL = '0.00';
const VALOR_MINIMO = 0;

const GridTipos = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-top: 16px;
    padding: 4px;
    overflow: hidden;
`;

const TipoCard = styled.button`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 24px;
    border: 1px solid ${({ theme }) => theme.colors.interfaceText100};
    border-radius: 8px;
    background: ${({ theme }) => theme.colors.interfacePure0};
    cursor: pointer;
    transition: all 0.2s ease;
    transform-origin: center;

    &:hover {
        transform: scale(1.02);
        border-color: ${({ theme }) => theme.colors.interfaceText200};
        background: ${({ theme }) => theme.colors.interfacePure100};
    }

    &:active {
        transform: scale(0.98);
    }

    span {
        font-size: 14px;
        font-weight: 500;
        color: ${({ theme }) => theme.colors.interfaceText400};
    }
`;

const ValorContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const TipoSelecionado = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background: ${({ theme }) => theme.colors.interfacePure100};
    border-radius: 8px;
    margin-bottom: 16px;
    
    span {
        font-size: 16px;
        font-weight: 500;
        color: ${({ theme }) => theme.colors.interfaceText400};
    }
`;

const DescricaoTipo = styled.p`
    font-size: 14px;
    color: ${({ theme }) => theme.colors.interfaceText300};
    margin: 0 0 16px 0;
    line-height: 1.5;
`;

const SelectWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const SelectTrigger = styled.button<{ isOpen: boolean }>`
    width: 100%;
    height: auto;
    padding: 8px 12px;
    border: 1px solid ${({ theme }) => theme.colors.interfaceText200};
    border-radius: 6px;
    background: ${({ theme }) => theme.colors.interfacePure0};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: all 0.2s ease;
    text-align: left;
    gap: 8px;

    &:hover {
        border-color: ${({ theme }) => theme.colors.interfaceText300};
    }

    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.colors.primaryRed400};
        box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primaryRed400}20;
    }
`;

const SelectValue = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
`;

const SelectValueName = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.interfaceText400};
    white-space: nowrap;
`;

const SelectValueBadge = styled.span<{ variant: 'correspondente' | 'solicitante' }>`
    font-size: 12px;
    padding: 2px 8px;
    border-radius: 4px;
    background: ${({ variant, theme }) => 
        variant === 'correspondente' 
            ? theme.colors.interfacePure100 
            : 'transparent'};
    color: ${({ theme }) => theme.colors.interfaceText400};
    border: 1px solid ${({ theme }) => theme.colors.interfaceText200};
    white-space: nowrap;
    font-weight: 400;
`;

const SelectDropdown = styled.div<{ isOpen: boolean }>`
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    background: ${({ theme }) => theme.colors.interfacePure0};
    border: 1px solid ${({ theme }) => theme.colors.interfaceText100};
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    max-height: 200px;
    overflow-y: auto;
    display: ${({ isOpen }) => isOpen ? 'block' : 'none'};
    width: 100%;
`;

const SelectItem = styled.button`
    width: 100%;
    padding: 12px 16px;
    border: none;
    background: transparent;
    cursor: pointer;
    text-align: left;
    transition: background 0.2s ease;
    display: flex;
    flex-direction: column;
    gap: 4px;

    &:hover {
        background: ${({ theme }) => theme.colors.interfacePure100};
    }

    &:first-child {
        border-radius: 6px 6px 0 0;
    }

    &:last-child {
        border-radius: 0 0 6px 6px;
    }
`;

const SelectItemRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const SelectItemName = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.interfaceText400};
`;

const SelectItemBadge = styled.span<{ variant: 'correspondente' | 'solicitante' }>`
    font-size: 12px;
    padding: 2px 8px;
    border-radius: 4px;
    background: ${({ variant, theme }) => 
        variant === 'correspondente' 
            ? theme.colors.interfacePure100 
            : 'transparent'};
    color: ${({ theme }) => theme.colors.interfaceText400};
    border: 1px solid ${({ theme }) => theme.colors.interfaceText200};
    font-weight: 400;
`;

const SelectItemEmail = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.colors.interfaceText300};
    font-weight: 400;
`;

const Overlay = styled.div<{ isOpen: boolean }>`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 999;
    display: ${({ isOpen }) => isOpen ? 'block' : 'none'};
`;

const ModalContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    max-height: 60vh;
    min-height: 0;
    overflow: hidden;
    overflow-x: hidden;
    width: 100%;
`;

const ModalContentScrollable = styled.div`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 12px;
    padding-left: 4px;
    min-height: 0;
    width: 100%;
    
    &::-webkit-scrollbar {
        width: 8px;
    }
    
    &::-webkit-scrollbar-track {
        background: ${({ theme }) => theme.colors.interfacePure100};
        border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.colors.interfaceText200};
        border-radius: 4px;
        
        &:hover {
            background: ${({ theme }) => theme.colors.interfaceText300};
        }
    }
`;

const ModalFooter = styled.div`
    display: flex;
    gap: 12px;
    padding: 16px 24px;
    border-top: 1px solid ${({ theme }) => theme.colors.interfacePure100};
    flex-shrink: 0;
    background: ${({ theme }) => theme.colors.interfacePure0};
    margin: 0;
`;

const ModalContentMain = styled(ModalcontactMain)`
    padding: 28px;
    gap: 0;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    overflow-x: hidden;
    width: 100%;
`;

const ModalCriarPendencia: React.FC<ModalCriarPendenciaProps> = ({
    modalAtivo,
    setModalAtivo,
    id_proposta,
    onPendenciaCriada
}) => {
    const { user } = useAuth();
    const [tipoSelecionado, setTipoSelecionado] = useState<string | null>(null);
    const [descricao, setDescricao] = useState<string>('');
    const [valor, setValor] = useState<string>(VALOR_INICIAL);
    const [criando, setCriando] = useState<boolean>(false);
    const [destinatario, setDestinatario] = useState<DestinatarioTipo>(null);
    const [selectOpen, setSelectOpen] = useState<boolean>(false);
    const [propostaData, setPropostaData] = useState<PropostaCompleta | null>(null);
    const [loadingProposta, setLoadingProposta] = useState<boolean>(false);
    const [solicitanteEmail, setSolicitanteEmail] = useState<string>('');

    const getTiposDisponiveis = useCallback((): TipoPendencia[] => {
        // ERRO 38: Solicitante, Operador e Admin só veem Informação, Orientação e Documentação
        const tiposPermitidosSolicitanteOperadorAdmin: string[] = [
            TIPOS_PENDENCIA.INFORMACAO,
            TIPOS_PENDENCIA.ORIENTACAO,
            TIPOS_PENDENCIA.DOCUMENTACAO
        ];

        const todosTipos: TipoPendencia[] = [
            {
                id: TIPOS_PENDENCIA.CORRECAO,
                nome: TIPOS_PENDENCIA_LABELS[TIPOS_PENDENCIA.CORRECAO],
                icon: getTipoIcon(TIPOS_PENDENCIA.CORRECAO),
                labelDescricao: TIPOS_PENDENCIA_LABELS_DESCRICAO[TIPOS_PENDENCIA.CORRECAO],
                temValor: false
            },
            {
                id: TIPOS_PENDENCIA.INFORMACAO,
                nome: TIPOS_PENDENCIA_LABELS[TIPOS_PENDENCIA.INFORMACAO],
                icon: getTipoIcon(TIPOS_PENDENCIA.INFORMACAO),
                labelDescricao: TIPOS_PENDENCIA_LABELS_DESCRICAO[TIPOS_PENDENCIA.INFORMACAO],
                temValor: false
            },
            {
                id: TIPOS_PENDENCIA.ORIENTACAO,
                nome: TIPOS_PENDENCIA_LABELS[TIPOS_PENDENCIA.ORIENTACAO],
                icon: getTipoIcon(TIPOS_PENDENCIA.ORIENTACAO),
                labelDescricao: TIPOS_PENDENCIA_LABELS_DESCRICAO[TIPOS_PENDENCIA.ORIENTACAO],
                temValor: false
            },
            {
                id: TIPOS_PENDENCIA.DOCUMENTACAO,
                nome: TIPOS_PENDENCIA_LABELS[TIPOS_PENDENCIA.DOCUMENTACAO],
                icon: getTipoIcon(TIPOS_PENDENCIA.DOCUMENTACAO),
                labelDescricao: TIPOS_PENDENCIA_LABELS_DESCRICAO[TIPOS_PENDENCIA.DOCUMENTACAO],
                temValor: false
            },
            {
                id: TIPOS_PENDENCIA.MAJORACAO,
                nome: TIPOS_PENDENCIA_LABELS[TIPOS_PENDENCIA.MAJORACAO],
                icon: getTipoIcon(TIPOS_PENDENCIA.MAJORACAO),
                labelDescricao: TIPOS_PENDENCIA_LABELS_DESCRICAO[TIPOS_PENDENCIA.MAJORACAO],
                temValor: true
            },
            {
                id: TIPOS_PENDENCIA.REEMBOLSO,
                nome: TIPOS_PENDENCIA_LABELS[TIPOS_PENDENCIA.REEMBOLSO],
                icon: getTipoIcon(TIPOS_PENDENCIA.REEMBOLSO),
                labelDescricao: TIPOS_PENDENCIA_LABELS_DESCRICAO[TIPOS_PENDENCIA.REEMBOLSO],
                temValor: true
            },
            {
                id: TIPOS_PENDENCIA.ADIANTAMENTO,
                nome: TIPOS_PENDENCIA_LABELS[TIPOS_PENDENCIA.ADIANTAMENTO],
                icon: getTipoIcon(TIPOS_PENDENCIA.ADIANTAMENTO),
                labelDescricao: TIPOS_PENDENCIA_LABELS_DESCRICAO[TIPOS_PENDENCIA.ADIANTAMENTO],
                temValor: true
            }
        ];

        // ERRO 38: Para Solicitante, Operador e Admin: apenas Informação, Orientação e Documentação
        if (user?.profile === 'operador' || user?.profile === 'admin' || 
            user?.profile === 'Solicitante' || user?.profile === 'solicitante') {
            return todosTipos.filter(t => tiposPermitidosSolicitanteOperadorAdmin.includes(t.id));
        }
        
        // Para Profissional (comum): todas as 6 pendências disponíveis (exceto Correção)
        return todosTipos.filter(t => t.id !== TIPOS_PENDENCIA.CORRECAO);
    }, [user?.profile]);

    const tiposDisponiveis = getTiposDisponiveis();

    const deveMostrarCampoPara = useMemo(() => {
        return user?.profile === 'admin' || user?.profile === 'operador';
    }, [user?.profile]);

    const temCorrespondente = useMemo(() => {
        if (!propostaData) return false;
        return !!(
            propostaData.id_usuario && 
            (propostaData.nomeProfissional || propostaData.nome_profissional)
        );
    }, [propostaData]);

    const temSolicitante = useMemo(() => {
        if (!propostaData) return false;
        return !!(
            propostaData.id_escritorio_solicitante && 
            propostaData.nome_escritorio
        );
    }, [propostaData]);

    const nomeCorrespondente = useMemo(() => {
        return propostaData?.nomeProfissional || propostaData?.nome_profissional || '';
    }, [propostaData]);

    const emailCorrespondente = useMemo(() => {
        return propostaData?.emailProfissional || propostaData?.email || '';
    }, [propostaData]);

    useEffect(() => {
        if (modalAtivo && deveMostrarCampoPara && !propostaData) {
            const carregarProposta = async () => {
                try {
                    setLoadingProposta(true);
                    const response = await api.get(`/proposta/${id_proposta}`);
                    if (response.data.status === 'success') {
                        setPropostaData(response.data.result);
                        
                        if (response.data.result.email_escritorio) {
                            setSolicitanteEmail(response.data.result.email_escritorio);
                        }
                    }
                } catch (error) {
                    console.error('Erro ao carregar proposta:', error);
                } finally {
                    setLoadingProposta(false);
                }
            };
            carregarProposta();
        }
    }, [modalAtivo, id_proposta, deveMostrarCampoPara, propostaData]);

    useEffect(() => {
        if (!propostaData || !destinatario) return;

        if (destinatario === 'correspondente' && !temCorrespondente) {
            setDestinatario(null);
        } else if (destinatario === 'solicitante' && !temSolicitante) {
            setDestinatario(null);
        }
    }, [propostaData, destinatario, temCorrespondente, temSolicitante]);

    const resetarFormulario = useCallback(() => {
        setTipoSelecionado(null);
        setDescricao('');
        setValor(VALOR_INICIAL);
        setDestinatario(null);
        setSelectOpen(false);
    }, []);

    const handleVoltar = useCallback(() => {
        resetarFormulario();
    }, [resetarFormulario]);

    const handleFechar = useCallback(() => {
        resetarFormulario();
        setPropostaData(null);
        setSolicitanteEmail('');
        setModalAtivo(false);
    }, [resetarFormulario, setModalAtivo]);

    const handleCriar = useCallback(async () => {
        if (!tipoSelecionado) return;

        const tipoInfo = tiposDisponiveis.find(t => t.id === tipoSelecionado);
        if (!tipoInfo) return;

        if (!descricao.trim()) {
            toast.error('Preencha a descrição', toastDefsError);
            return;
        }

        if (tipoInfo.temValor && (!valor || valor === VALOR_INICIAL || parseFloat(valor) === VALOR_MINIMO)) {
            toast.error('Preencha o valor', toastDefsError);
            return;
        }

        try {
            setCriando(true);
            const payload: any = {
                id_proposta,
                tipo: tipoSelecionado,
                descricao: descricao.trim()
            };

            if (tipoInfo.temValor) {
                payload.valor = parseFloat(valor);
            }

            if (deveMostrarCampoPara && destinatario && propostaData) {
                if (destinatario === 'correspondente' && propostaData.id_usuario) {
                    payload.id_destino = propostaData.id_usuario;
                    payload.destino = 'profissional';
                } else if (destinatario === 'solicitante' && propostaData.id_escritorio_solicitante) {
                    payload.id_destino = propostaData.id_escritorio_solicitante;
                    payload.destino = 'solicitante';
                }
            }

            const response = await api.post('/pendencia', payload);

            if (response.data.status !== 'success') {
                const errorMessage = response.data.result?.[0]?.error || response.data.message || 'Erro ao criar pendência';
                throw new Error(errorMessage);
            }

            toast.success('Pendência criada com sucesso', toastDefs);
            handleFechar();
            
            if (onPendenciaCriada) {
                onPendenciaCriada();
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || 'Erro ao criar pendência';
            toast.error(errorMessage, toastDefsError);
        } finally {
            setCriando(false);
        }
    }, [tipoSelecionado, descricao, valor, id_proposta, tiposDisponiveis, onPendenciaCriada, handleFechar, deveMostrarCampoPara, destinatario, propostaData]);

    const tipoInfo = tipoSelecionado ? tiposDisponiveis.find(t => t.id === tipoSelecionado) : null;
    const IconComponent = tipoInfo?.icon || FileText;

    return (
        <ModalDefault
            id={'overlayModalCriarPendencia'}
            onClose={handleFechar}
            openModal={modalAtivo}
            width="fit-content"
            maxWidth="600px"
        >
            <ModalContactHead>
                <div>
                    Nova Pendência
                </div>
                <CloseButtonHeader onClick={handleFechar}>
                    <IconClose color={comum.colors.primaryRed400} />
                </CloseButtonHeader>
            </ModalContactHead>

            <ModalContentWrapper>
                <ModalContentMain>
                    {!tipoSelecionado ? (
                        <ModalContentScrollable>
                            <GridTipos>
                                {tiposDisponiveis.map((tipo) => {
                                    const Icon = tipo.icon;
                                    return (
                                        <TipoCard
                                            key={tipo.id}
                                            onClick={() => setTipoSelecionado(tipo.id)}
                                        >
                                            <Icon size={32} color={comum.colors.primaryRed400} />
                                            <span>{tipo.nome}</span>
                                        </TipoCard>
                                    );
                                })}
                            </GridTipos>
                        </ModalContentScrollable>
                    ) : (
                        <>
                            <ModalContentScrollable>
                                <TipoSelecionado>
                                    <IconComponent size={20} color={comum.colors.primaryRed400} />
                                    <span>{tipoInfo?.nome}</span>
                                </TipoSelecionado>

                                {tipoSelecionado && TIPOS_PENDENCIA_DESCRICOES[tipoSelecionado] && (
                                    <DescricaoTipo>
                                        {TIPOS_PENDENCIA_DESCRICOES[tipoSelecionado]}
                                    </DescricaoTipo>
                                )}

                                <ModalSection>
                                    {deveMostrarCampoPara && propostaData && (
                                        <div>
                                            <Label>Para:</Label>
                                            <SelectWrapper>
                                                <SelectTrigger 
                                                    type="button"
                                                    isOpen={selectOpen}
                                                    onClick={() => setSelectOpen(!selectOpen)}
                                                    aria-label="Selecionar destinatário"
                                                    aria-expanded={selectOpen}
                                                >
                                                    <SelectValue>
                                                        {destinatario && (
                                                            (destinatario === 'correspondente' && temCorrespondente) ||
                                                            (destinatario === 'solicitante' && temSolicitante)
                                                        ) ? (
                                                            <>
                                                                <SelectValueName>
                                                                    {destinatario === 'correspondente' 
                                                                        ? nomeCorrespondente
                                                                        : propostaData.nome_escritorio}
                                                                </SelectValueName>
                                                                <SelectValueBadge variant={destinatario}>
                                                                    {destinatario === 'correspondente' ? 'Profissional' : 'Solicitante'}
                                                                </SelectValueBadge>
                                                            </>
                                                        ) : (
                                                            <SelectValueName style={{ color: comum.colors.interfaceText300 }}>
                                                                Selecione...
                                                            </SelectValueName>
                                                        )}
                                                    </SelectValue>
                                                    <ChevronDown size={16} color={comum.colors.interfaceText300} />
                                                </SelectTrigger>
                                                <SelectDropdown isOpen={selectOpen} role="listbox">
                                                    {temCorrespondente && (
                                                        <SelectItem
                                                            type="button"
                                                            role="option"
                                                            onClick={() => {
                                                                setDestinatario('correspondente');
                                                                setSelectOpen(false);
                                                            }}
                                                        >
                                                            <SelectItemRow>
                                                                <SelectItemName>
                                                                    {nomeCorrespondente}
                                                                </SelectItemName>
                                                                <SelectItemBadge variant="correspondente">
                                                                    Profissional
                                                                </SelectItemBadge>
                                                            </SelectItemRow>
                                                            {emailCorrespondente && (
                                                                <SelectItemEmail>
                                                                    {emailCorrespondente}
                                                                </SelectItemEmail>
                                                            )}
                                                        </SelectItem>
                                                    )}
                                                    {temSolicitante && (
                                                        <SelectItem
                                                            type="button"
                                                            role="option"
                                                            onClick={() => {
                                                                setDestinatario('solicitante');
                                                                setSelectOpen(false);
                                                            }}
                                                        >
                                                            <SelectItemRow>
                                                                <SelectItemName>
                                                                    {propostaData.nome_escritorio}
                                                                </SelectItemName>
                                                                <SelectItemBadge variant="solicitante">
                                                                    Solicitante
                                                                </SelectItemBadge>
                                                            </SelectItemRow>
                                                            {solicitanteEmail && (
                                                                <SelectItemEmail>
                                                                    {solicitanteEmail}
                                                                </SelectItemEmail>
                                                            )}
                                                        </SelectItem>
                                                    )}
                                                </SelectDropdown>
                                            </SelectWrapper>
                                        </div>
                                    )}

                                    {tipoInfo?.temValor && (
                                        <ValorContainer>
                                            <Label>Valor (R$)</Label>
                                            <InputMoney
                                                name="valor"
                                                placeholder="R$ 0,00"
                                                value={valor}
                                                onChangeText={setValor}
                                            />
                                        </ValorContainer>
                                    )}

                                    <div>
                                        <Label>{tipoInfo?.labelDescricao}</Label>
                                        <TextArea
                                            placeholder="Digite a descrição..."
                                            value={descricao}
                                            onChange={(e) => setDescricao(e.target.value)}
                                            rows={5}
                                        />
                                    </div>
                                </ModalSection>
                            </ModalContentScrollable>

                            <ModalFooter>
                                <Button variant="secondary" onClick={handleVoltar} disabled={criando} style={{ flex: 1 }}>
                                    Voltar
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleCriar}
                                    disabled={
                                        criando || 
                                        !descricao.trim() || 
                                        (tipoInfo?.temValor && (!valor || valor === VALOR_INICIAL || parseFloat(valor) === VALOR_MINIMO)) ||
                                        !!(deveMostrarCampoPara && propostaData && (temCorrespondente || temSolicitante) && !destinatario)
                                    }
                                    style={{ flex: 1 }}
                                >
                                    {criando ? 'Solicitando...' : 'Solicitar'}
                                </Button>
                            </ModalFooter>
                        </>
                    )}
                </ModalContentMain>
            </ModalContentWrapper>
            <Overlay isOpen={selectOpen} onClick={() => setSelectOpen(false)} />
        </ModalDefault>
    );
};

export default ModalCriarPendencia;

